"use client";

import { motion } from "framer-motion";
import productImg from "@/assets/product-hero.jpg";
import { ArrowRight, Check } from "lucide-react";
import { formatPrice } from "@/lib/formatters";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const MotionButton = motion(Button);

export function Pricing() {
  const containerVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: [0.21, 0.47, 0.32, 0.98],
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  return (
    <section id="pricing" className="py-28 lg:py-32 bg-surface overflow-hidden">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <motion.div
          className="bg-card border border-border rounded-3xl overflow-hidden grid lg:grid-cols-2"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <div className="bg-gradient-to-br from-foreground to-foreground/80 p-8 lg:p-14 flex items-center justify-center">
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              src={productImg.src}
              alt="Apollo G3"
              width={1400}
              height={1400}
              loading="lazy"
              className="w-full max-w-sm aspect-[4/3] lg:aspect-square object-cover rounded-2xl"
            />
          </div>
          <div className="p-8 lg:p-14 flex flex-col">
            <motion.p variants={itemVariants} className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-primary mb-4">Launch offer</motion.p>
            <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl font-semibold tracking-tight">Apollo G3</motion.h2>
            <motion.p variants={itemVariants} className="mt-3 text-muted-foreground text-sm sm:text-base">The complete smart home hub. One device. Every room.</motion.p>

            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-3 overflow-visible py-2">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl sm:text-5xl font-semibold tracking-tight whitespace-nowrap">{formatPrice(34900)}</span>
                <span className="text-lg sm:text-xl text-muted-foreground line-through whitespace-nowrap">{formatPrice(44900)}</span>
              </div>
              <motion.div variants={itemVariants} className="sm:ml-auto">
                <Badge variant="secondary" className="bg-primary/10 text-primary border-none px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-bold whitespace-nowrap">
                  Save {formatPrice(10000)}
                </Badge>
              </motion.div>
            </motion.div>

            <ul className="mt-8 space-y-4 text-sm">
              {["Free 2-day shipping", "90-day home trial", "2-year warranty", "Lifetime software updates"].map((p, idx) => (
                <motion.li
                  key={p}
                  variants={itemVariants}
                  className="flex items-start gap-3 text-foreground/80 font-medium leading-tight"
                >
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={3} />
                  <span>{p}</span>
                </motion.li>
              ))}
            </ul>

            <MotionButton
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-10 w-full rounded-full bg-primary text-primary-foreground h-14 text-sm font-bold hover:opacity-90 transition shadow-lg shadow-primary/20"
            >
              Buy Now <ArrowRight className="h-4 w-4" />
            </MotionButton>
            <motion.p variants={itemVariants} className="mt-4 text-center text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground leading-relaxed px-4">
              Ships within 48 hours · Free returns
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
